import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { fetchWrapper } from "../../utils/fetchWrapper";
import Loader from "../../utils/Loader";
import "../../assets/css/admin/visitorList.css";

const READ_KEY = "portfolio_admin_read_visitors";

const SPAM_PATTERNS = [
  /\b(viagra|cialis|casino|lottery|crypto\s*airdrop|work\s*from\s*home|click\s*here|earn\s*\$)\b/i,
  /\b(SEO|backlink|guest\s*post)\s*(service|offer)\b/i,
];

function loadReadIds() {
  try {
    const raw = localStorage.getItem(READ_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

function saveReadIds(ids) {
  localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
}

function getInitials(name) {
  const parts = String(name ?? "")
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (!parts.length) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function shortenId(id) {
  const s = String(id ?? "");
  if (s.length <= 10) return s;
  return `${s.slice(0, 6)}…${s.slice(-4)}`;
}

function isToday(dateValue) {
  if (!dateValue) return false;
  const d = new Date(dateValue);
  if (Number.isNaN(d.getTime())) return false;
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate()
  );
}

function isSpam(visitor) {
  const message = String(visitor?.message ?? "");
  const email = String(visitor?.email ?? "");
  const name = String(visitor?.name ?? "");

  if (SPAM_PATTERNS.some((re) => re.test(message) || re.test(email) || re.test(name))) {
    return true;
  }

  const linkCount = (message.match(/https?:\/\//gi) ?? []).length;
  if (linkCount >= 2) return true;
  if (linkCount >= 1 && message.length < 50) return true;

  const freeMailBurst =
    /@(tempmail|guerrillamail|mailinator|yopmail|10minutemail)\./i.test(email);
  if (freeMailBurst) return true;

  if (message.length > 20 && message === message.toUpperCase() && /[A-Z]/.test(message)) {
    return true;
  }

  return false;
}

function SearchIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="11" cy="11" r="7" />
      <path d="M21 21l-4.2-4.2" strokeLinecap="round" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <rect x="3" y="5" width="18" height="16" rx="2" />
      <path d="M8 3v4M16 3v4M3 11h18" strokeLinecap="round" />
    </svg>
  );
}

function HeroArt() {
  return (
    <svg
      className="visitor-list__hero-art"
      viewBox="0 0 200 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <rect x="8" y="16" width="184" height="88" rx="12" fill="rgba(255,255,255,0.08)" />
      <circle cx="52" cy="52" r="18" fill="rgba(195,88,136,0.35)" />
      <rect x="80" y="40" width="88" height="8" rx="4" fill="rgba(255,255,255,0.5)" />
      <rect x="80" y="56" width="64" height="6" rx="3" fill="rgba(255,255,255,0.28)" />
      <rect x="24" y="78" width="152" height="18" rx="6" fill="rgba(255,255,255,0.12)" />
      <circle cx="168" cy="36" r="6" fill="#c35888" opacity="0.9" />
    </svg>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="visitor-list__stat-card">
      <p className="visitor-list__stat-label">{label}</p>
      <p className="visitor-list__stat-value">{value}</p>
      {hint ? <p className="visitor-list__stat-hint">{hint}</p> : null}
    </div>
  );
}

function VisitorList() {
  const [firstId, setFirstId] = useState(null);
  const [lastId, setLastId] = useState(null);
  const [direction, setDirection] = useState("next");
  const [pageSize, setPageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [readIds, setReadIds] = useState(() => loadReadIds());
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [openMenuId, setOpenMenuId] = useState(null);

  const markRead = (id) => {
    const key = String(id);
    setReadIds((prev) => {
      const next = new Set(prev);
      next.add(key);
      saveReadIds(next);
      return next;
    });
  };

  const fetchVisitors = async () => {
    const params = new URLSearchParams();
    params.set("limit", String(pageSize));
    if (direction === "next" && lastId) params.append("lastId", lastId);
    if (direction === "prev" && firstId) params.append("firstId", firstId);

    const response = await fetchWrapper(
      `${process.env.REACT_APP_BACKEND_API_URL}/visitors/?${params.toString()}`,
      { method: "GET" }
    );
    return response.data;
  };

  const fetchStatsSample = async () => {
    const params = new URLSearchParams({ limit: "100" });
    const response = await fetchWrapper(
      `${process.env.REACT_APP_BACKEND_API_URL}/visitors/?${params.toString()}`,
      { method: "GET" }
    );
    return response.data;
  };

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["visitors", direction, firstId, lastId, pageSize],
    queryFn: fetchVisitors,
    placeholderData: (previousData) => previousData,
    staleTime: 30_000,
    gcTime: 60_000,
  });

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ["visitors-stats"],
    queryFn: fetchStatsSample,
    staleTime: 60_000,
    gcTime: 120_000,
  });

  const exportMutation = useMutation({
    mutationFn: () =>
      fetchWrapper(`${process.env.REACT_APP_BACKEND_API_URL}/visitors/export`, {
        method: "GET",
      }),
  });

  const stats = useMemo(() => {
    const sample = statsData?.visitors ?? [];
    const totalExact = !statsData?.hasNext;
    const total = totalExact ? String(sample.length) : `${sample.length}+`;
    const today = sample.filter((v) => isToday(v.createdAt)).length;
    const unread = sample.filter((v) => !readIds.has(String(v._id))).length;
    const spam = sample.filter(isSpam).length;
    return { total, today, unread, spam, sampleNote: totalExact ? null : "Based on latest 100" };
  }, [statsData, readIds]);

  useEffect(() => {
    window.dispatchEvent(
      new CustomEvent("portfolio-admin-unread", { detail: stats.unread })
    );
  }, [stats.unread]);

  const pageVisitors = data?.visitors ?? [];

  const filteredRecords = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pageVisitors.filter((v) => {
      if (statusFilter === "new" && readIds.has(String(v._id))) return false;
      if (statusFilter === "read" && !readIds.has(String(v._id))) return false;
      if (statusFilter === "spam" && !isSpam(v)) return false;
      if (!q) return true;
      const hay = [v.name, v.email, v.message]
        .map((x) => String(x ?? "").toLowerCase())
        .join(" ");
      return hay.includes(q);
    });
  }, [pageVisitors, search, statusFilter, readIds]);

  const hasPageRows = pageVisitors.length > 0;
  const searchNoMatch = hasPageRows && filteredRecords.length === 0;
  const allSelected =
    filteredRecords.length > 0 &&
    filteredRecords.every((v) => selectedIds.has(String(v._id)));

  const rangeStart = hasPageRows ? (pageIndex - 1) * pageSize + 1 : 0;
  const rangeEnd = hasPageRows ? rangeStart + filteredRecords.length - 1 : 0;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(filteredRecords.map((v) => String(v._id))));
  };

  const toggleSelect = (id) => {
    const key = String(id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const resetPagination = () => {
    setFirstId(null);
    setLastId(null);
    setDirection("next");
    setPageIndex(1);
  };

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    resetPagination();
  };

  if (isLoading && !data) return <Loader isLoading />;

  const disablePrev = isFetching || !data?.hasPrev;
  const disableNext = isFetching || !data?.hasNext;

  const heroBlock = (
    <header className="visitor-list__hero visitor-list__hero--split">
      <div className="visitor-list__hero-text">
        <h1>Visitors</h1>
        <p>
          Inbound messages from your portfolio contact flow. Review new submissions, mark them read,
          and export records when you need a backup.
        </p>
      </div>
      <HeroArt />
    </header>
  );

  if (isError) {
    return (
      <div className="visitor-list">
        <div className="visitor-list__inner">
          {heroBlock}
          <div className="visitor-list__empty visitor-list__empty--error">
            <h2>Could not load visitors</h2>
            <p>{error?.message || "Check that the backend is running and REACT_APP_BACKEND_API_URL is set."}</p>
            <button type="button" className="visitor-list__btn visitor-list__btn--primary" onClick={() => refetch()}>
              Try again
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPageRows && !isFetching) {
    return (
      <div className="visitor-list">
        <div className="visitor-list__inner">
          {heroBlock}
          <div className="visitor-list__stats visitor-list__stats--loading">
            <StatCard label="Total Visitors" value={statsLoading ? "…" : stats.total} hint={stats.sampleNote} />
            <StatCard label="Visitors Today" value={statsLoading ? "…" : stats.today} />
            <StatCard label="Unread" value={statsLoading ? "…" : stats.unread} />
            <StatCard label="Spam Detected" value={statsLoading ? "…" : stats.spam} />
          </div>
          <div className="visitor-list__empty">
            <h2>No visitors yet</h2>
            <p>When someone reaches out through your site, their details will be listed in this table.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="visitor-list">
      <div className="visitor-list__inner">
        {heroBlock}

        <div className="visitor-list__stats">
          <StatCard label="Total Visitors" value={statsLoading ? "…" : stats.total} hint={stats.sampleNote} />
          <StatCard label="Visitors Today" value={statsLoading ? "…" : stats.today} />
          <StatCard label="Unread" value={statsLoading ? "…" : stats.unread} />
          <StatCard label="Spam Detected" value={statsLoading ? "…" : stats.spam} />
        </div>

        <div className="visitor-list__card">
          <div className="visitor-list__toolbar">
          <div className="visitor-list__search-wrap">
            <SearchIcon />
            <input
              className="visitor-list__search"
              type="search"
              value={search}
              placeholder="Search name, email, or message…"
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search visitors"
            />
          </div>

          <button type="button" className="visitor-list__btn visitor-list__btn--ghost" disabled title="Coming soon">
            <CalendarIcon />
            Date range
          </button>

          <select
            className="visitor-list__select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            <option value="new">New</option>
            <option value="read">Read</option>
            <option value="spam">Spam</option>
          </select>

          <button
            type="button"
            className="visitor-list__btn visitor-list__btn--accent"
            disabled={exportMutation.isPending}
            onClick={() => exportMutation.mutate()}
          >
            {exportMutation.isPending ? "Exporting…" : "Export CSV"}
          </button>
          </div>

          <div className="visitor-list__table-wrap">
            <table className="visitor-list__table visitor-list__table--modern">
              <caption>Visitor messages</caption>
              <thead>
                <tr>
                  <th scope="col" className="visitor-list__col-check">
                    <input
                      type="checkbox"
                      checked={allSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all on this page"
                    />
                  </th>
                  <th scope="col">Visitor</th>
                  <th scope="col">Email</th>
                  <th scope="col">Message</th>
                  <th scope="col">Status</th>
                  <th scope="col">Received</th>
                  <th scope="col" className="visitor-list__col-actions">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {searchNoMatch ? (
                  <tr>
                    <td colSpan={7}>
                      <p className="visitor-list__search-match">
                        No visitor on this page matches your filters. Clear search or change the status filter.
                      </p>
                    </td>
                  </tr>
                ) : (
                  filteredRecords.map((visitor) => {
                    const id = String(visitor._id);
                    const read = readIds.has(id);
                    const spam = isSpam(visitor);
                    return (
                      <tr key={id} className={read ? "visitor-list__row--read" : "visitor-list__row--new"}>
                        <td>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(id)}
                            onChange={() => toggleSelect(id)}
                            aria-label={`Select ${visitor.name || "visitor"}`}
                          />
                        </td>
                        <td>
                          <div className="visitor-list__visitor-cell">
                            <span className="visitor-list__avatar" aria-hidden>
                              {getInitials(visitor.name)}
                            </span>
                            <span className="visitor-list__visitor-meta">
                              <span className="visitor-list__name">{visitor.name ?? "—"}</span>
                              <span className="visitor-list__id" title={id}>
                                {shortenId(id)}
                              </span>
                            </span>
                          </div>
                        </td>
                        <td className="visitor-list__email">
                          {visitor.email ? (
                            <a href={`mailto:${visitor.email}`}>{visitor.email}</a>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td>
                          <div
                            className={`visitor-list__message${spam ? " visitor-list__message--spam" : ""}`}
                            title={visitor.message || undefined}
                          >
                            {visitor.message ?? "—"}
                          </div>
                        </td>
                        <td>
                          <span
                            className={`visitor-list__badge ${
                              read ? "visitor-list__badge--read" : "visitor-list__badge--new"
                            }`}
                          >
                            {read ? "Read" : "New"}
                          </span>
                        </td>
                        <td>
                          <span className="visitor-list__time">
                            {visitor.createdAt
                              ? new Date(visitor.createdAt).toLocaleString(undefined, {
                                  year: "numeric",
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : "—"}
                          </span>
                        </td>
                        <td className="visitor-list__col-actions">
                          <div className="visitor-list__actions">
                            <button
                              type="button"
                              className="visitor-list__menu-btn"
                              aria-expanded={openMenuId === id}
                              aria-haspopup="menu"
                              onClick={(e) => {
                                e.stopPropagation();
                                setOpenMenuId((prev) => (prev === id ? null : id));
                              }}
                            >
                              ⋯
                            </button>
                            {openMenuId === id ? (
                              <div className="visitor-list__menu" role="menu">
                                {!read ? (
                                  <button
                                    type="button"
                                    role="menuitem"
                                    onClick={() => {
                                      markRead(id);
                                      setOpenMenuId(null);
                                    }}
                                  >
                                    Mark read
                                  </button>
                                ) : null}
                                {visitor.email ? (
                                  <a
                                    role="menuitem"
                                    href={`mailto:${visitor.email}?subject=Re: Your message`}
                                    onClick={() => setOpenMenuId(null)}
                                  >
                                    Reply via email
                                  </a>
                                ) : null}
                              </div>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <footer className="visitor-list__footer">
            <p className="visitor-list__range">
              {filteredRecords.length
                ? `Showing ${rangeStart} to ${rangeEnd} results`
                : "No results on this page"}
              {isFetching ? " · Loading…" : ""}
            </p>
            <div className="visitor-list__pagination">
              <button
                type="button"
                className="visitor-list__btn visitor-list__btn--primary"
                disabled={disablePrev}
                onClick={() => {
                  setDirection("prev");
                  setFirstId(data.firstId);
                  setLastId(data.lastId);
                  setPageIndex((p) => Math.max(1, p - 1));
                }}
              >
                Previous
              </button>
              <button
                type="button"
                className="visitor-list__btn visitor-list__btn--primary"
                disabled={disableNext}
                onClick={() => {
                  setDirection("next");
                  setLastId(data.lastId);
                  setFirstId(data.firstId);
                  setPageIndex((p) => p + 1);
                }}
              >
                Next
              </button>
            </div>
            <label className="visitor-list__page-size">
              <span>Rows per page</span>
              <select value={pageSize} onChange={handlePageSizeChange} aria-label="Rows per page">
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
              </select>
            </label>
          </footer>
        </div>
      </div>
    </div>
  );
}

export default VisitorList;
